// components/funnel/FunnelActivation.jsx
import React from "react";
import {
  Filter, Sparkles, TrendingUp, TrendingDown, Layers, Award,
  Target, Info, AlertTriangle, ArrowLeft, Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";

function FunnelActivation({ activating, onActivate }) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  /* ---------- Feature cards ---------- */
  const features = [
    {
      icon: Target,
      title: t("funnelActivation.features.conversionRate.title"),
      desc: t("funnelActivation.features.conversionRate.desc"),
      tone: "teal",
    },
    {
      icon: TrendingDown,
      title: t("funnelActivation.features.churnRate.title"),
      desc: t("funnelActivation.features.churnRate.desc"),
      tone: "amber",
    },
    {
      icon: Layers,
      title: t("funnelActivation.features.stageRate.title"),
      desc: t("funnelActivation.features.stageRate.desc"),
      tone: "teal",
    },
    {
      icon: Award,
      title: t("funnelActivation.features.loyalCustomers.title"),
      desc: t("funnelActivation.features.loyalCustomers.desc"),
      tone: "amber",
    },
  ];

  const tones = {
    teal: {
      wrap: "border-teal-500/25 dark:border-teal-400/20",
      icon: "from-teal-400/40 to-teal-600/20 border-teal-400/50 text-teal-700 dark:text-teal-300",
      title: "text-teal-700 dark:text-teal-300",
      hover: "hover:border-teal-400/60 hover:bg-teal-500/5",
    },
    amber: {
      wrap: "border-amber-500/25 dark:border-amber-400/20",
      icon: "from-amber-300/40 to-amber-500/20 border-amber-300/50 text-amber-700 dark:text-amber-300",
      title: "text-amber-700 dark:text-amber-300",
      hover: "hover:border-amber-400/60 hover:bg-amber-300/5",
    },
  };

  return (
    <div className="flex min-h-[500px] items-center justify-center px-2 sm:px-4">
      <div
        className="
          relative w-full max-w-2xl overflow-hidden
          rounded-3xl
          bg-white/70 dark:bg-slate-900/70
          backdrop-blur-xl
          border border-teal-500/20 dark:border-teal-400/15
          shadow-[0_20px_60px_-20px_rgba(15,118,110,0.4)]
          dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]
        "
        style={{ animation: "fadeInUp 0.6s ease-out" }}
      >
        {/* خط طلایی بالا */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-l from-transparent via-amber-400/90 to-transparent z-10" />

        {/* گلوه‌های تزئینی */}
        <div className="pointer-events-none absolute -top-32 -right-32 h-80 w-80 rounded-full bg-amber-300/20 dark:bg-amber-400/10 blur-3xl animate-float" />
        <div
          className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-teal-400/20 dark:bg-teal-500/10 blur-3xl animate-float"
          style={{ animationDelay: "1.5s" }}
        />

        <div className="relative p-6 sm:p-8 md:p-10 text-center">

          {/* ============ Icon ============ */}
          <div className="relative mx-auto mb-6 w-fit">
            {/* Pulse ring */}
            <span
              className="absolute inset-0 -m-3 rounded-full bg-amber-400/20 animate-ping"
              style={{ animationDuration: "2.5s" }}
            />
            <div className="relative flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-amber-300/30 via-amber-400/20 to-teal-400/20 border-2 border-amber-300/50 dark:border-amber-400/30 shadow-[0_15px_40px_-15px_rgba(251,191,36,0.6)]">
              <Filter
                className="h-9 w-9 sm:h-11 sm:w-11 text-amber-600 dark:text-amber-300 drop-shadow"
                strokeWidth={1.8}
              />
              <Sparkles className="absolute -top-1.5 -right-1.5 h-5 w-5 text-amber-400 animate-float" />
            </div>
          </div>

          {/* ============ Title ============ */}
          <h2 className="text-xl sm:text-2xl  font-extrabold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:from-amber-200 dark:via-teal-300 dark:to-teal-400 bg-clip-text text-transparent">
            {t("funnelActivation.title")}
          </h2>

          <div className="mx-auto mt-4 mb-5 h-px w-24 bg-gradient-to-l from-transparent via-amber-400/60 to-transparent" />

          {/* ============ Description ============ */}
          <p className="mx-auto max-w-lg text-xs sm:text-sm leading-7 text-slate-600 dark:text-slate-400">
            {t("funnelActivation.description")}
          </p>

          {/* ============ Warning ============ */}
          <div className="mx-auto mt-5 flex max-w-lg items-start gap-2.5 rounded-2xl bg-amber-400/15 dark:bg-amber-500/10 border border-amber-400/40 dark:border-amber-400/25 px-4 py-3 text-right">
            <span className="shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 shadow-md">
              <AlertTriangle size={11} className="text-white" />
            </span>
            <p className="text-[11px] sm:text-xs font-medium text-amber-800 dark:text-amber-200 leading-6">
              {t("funnelActivation.warning")}
            </p>
          </div>

          {/* ============ Features Grid ============ */}
          <div className="mx-auto mt-6 grid max-w-lg grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-right">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              const tone = tones[feature.tone];
              return (
                <div
                  key={i}
                  className={`
                    group relative overflow-hidden
                    flex items-start gap-3
                    rounded-2xl border ${tone.wrap}
                    bg-white/60 dark:bg-white/5
                    backdrop-blur-md
                    p-3.5
                    transition-all duration-300
                    ${tone.hover}
                    hover:scale-[1.02]
                  `}
                  style={{ animation: `fadeInUp 0.5s ease-out ${0.15 + i * 0.08}s both` }}
                >
                  <span
                    className={`
                      shrink-0 flex h-9 w-9 items-center justify-center rounded-xl
                      bg-gradient-to-br ${tone.icon}
                      border
                      transition-transform duration-300
                      group-hover:scale-110 group-hover:rotate-3
                    `}
                  >
                    <Icon size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className={`text-xs sm:text-sm font-extrabold text-start ${tone.title}`}>
                      {feature.title}
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 leading-5 mt-0.5">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ============ CTA Button ============ */}
          <button
            onClick={onActivate}
            disabled={activating}
            className="
              group relative overflow-hidden
              mt-8 w-full max-w-lg mx-auto
              flex items-center justify-center gap-2.5
              rounded-2xl px-6 py-3.5
              text-sm sm:text-base font-bold text-white
              bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
              bg-[length:200%_100%] hover:bg-[position:100%_0]
              shadow-[0_10px_30px_-8px_rgba(15,118,110,0.8)]
              hover:shadow-[0_15px_40px_-8px_rgba(20,184,166,1)]
              transition-all duration-500
              hover:scale-[1.02] active:scale-[0.98]
              disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
            "
          >
            {/* Shimmer */}
            {!activating && (
              <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
            )}

            {activating ? (
              <>
                <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                <span>{t("funnelActivation.activating")}</span>
              </>
            ) : (
              <>
                <Zap size={16} className="relative" />
                <span className="relative">{t("funnelActivation.activate")}</span>
                <ArrowLeft
                  size={16}
                  className="relative opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                />
              </>
            )}
          </button>

          {/* ============ Footer note ============ */}
          <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] sm:text-[11px] text-slate-400 dark:text-slate-500">
            <Info size={11} className="text-teal-600/70 dark:text-teal-300/70 shrink-0" />
            <span>{t("funnelActivation.footerNote")}</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-15px); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

export default FunnelActivation;